<template>
	<div>
		<b-card>
			<template slot="header">
				<h4 class="mt-2 custom-card-header-text">Reports</h4>
			</template>
			<b-card-body>
                <div class="d-flex mb-3 controls align-items-center">
                    <!-- Search Section -->
                    <search-section search-placeholder="Search Reports..." @search="handleSearch" @limit-change="handleLimitChange">
                        <!-- Community & Circle Filter Slot - positioned before Records dropdown -->
                        <template v-slot:before-records>
                            <b-form-group
                                v-if="communities.length > 1"
                                label="Community:"
                                label-for="community-filter"
                                class="mb-0 limit-group mr-3"
                            >
                                <b-form-select
                                    id="community-filter"
                                    v-model="selectedCommunity"
                                    @change="onCommunityFilterChange"
                                    class="custom-limit-select"
                                >
                                    <option value="all">All</option>
                                    <option v-for="community in communities" :key="community._id" :value="community._id">
                                        {{ community.name }}
                                    </option>
                                </b-form-select>
                            </b-form-group>
                            <b-form-group
                                v-if="circles.length > 0"
                                label="Circle:"
                                label-for="circle-filter"
                                class="mb-0 limit-group mr-3"
                            >
                                <b-form-select
                                    id="circle-filter"
                                    v-model="selectedCircle"
                                    class="custom-limit-select"
                                >
                                    <option value="all">All</option>
                                    <option value="none">No Circle</option>
                                    <option v-for="circle in circles" :key="circle.short" :value="circle.short">
                                        {{ circle.name }}
                                    </option>
                                </b-form-select>
                            </b-form-group>
                        </template>
                    </search-section>
                </div>
                
				<v-client-table ref="reportsTable" v-bind:data="reports" v-bind:columns="columns" :options="options" id="dataTable" class="reports-table">
					<span slot="name" slot-scope="props">
                    {{ props.row.name }}
                    </span>
					<span slot="description" slot-scope="props">
                    {{ props.row.description }}
                    </span>
                    <span slot="date range filter" slot-scope="props">
                        <div class="row-bak d-flex-bak align-items-center-bak">
							<datepicker @changed="dateFilter($event, props.row.type)"
							style="padding-right: 10px;" label=""></datepicker>
						</div>
                        <!-- SuperAdmin only: customers always get self opt-outs (enforced in core-api too). -->
                        <div v-if="isSuperAdmin && props.row.type === reportTypes.OptOut" class="mt-2 d-flex justify-content-center">
                            <label for="opt-out-scope" class="sr-only">Opt-outs to include</label>
                            <!-- Same class/width as the date filter's select above so they line up. -->
                            <b-form-select id="opt-out-scope" v-model="optOutScope" class="filter" style="width: auto;">
                                <option value="self">Self opt-outs</option>
                                <option value="all">All unsubscribes</option>
                            </b-form-select>
                        </div>
                    </span>
                    <span slot="action" slot-scope="props" class="action-width">
                        <button type="button" aria-label="Export" aria-live="assertive" @click="exportData(props.row.type)" :title="downloadTooltip" class="btn custom-card-header-filter btn-dropbox tab-focus"><i class="icon-cloud-download"></i></button>
                    </span>
				</v-client-table>
			</b-card-body>
		</b-card>


	</div>
</template>

<script>

import VueNotifications from 'vue-notifications';
import datepicker from '../engagement/components/datePicker';
import { reportType } from './constants/reportConstant';
import { exportCSV, toDayParam, pickedDayToUtc } from '@/assets/js/common';
import SearchSection from '@/views/engagement/components/searchSection.vue';

export default {
    name: 'reports',
    components: {
		datepicker,
        SearchSection
	},
    data() {
        return {
            token: '',
            reports: [],
            columns: ['name', 'description', 'date range filter','action'],
            options: {
                sortable: ['name', 'description'],
                sortIcon: { base:'fa', up:'fa-sort-asc', down:'fa-sort-desc', is:'fa-sort' },
                pagination: {
					show: false,
					dropdown: false,
                    chunk: 10,
                    nav: 'fixed'
                },
				filterable: true,
				perPageDropdown: [],
            },
            // Community filter state
            selectedCommunity: 'all',
            communities: [],
            // Circle filter state
            selectedCircle: 'all',
            circles: [],
            reportTypes: reportType,
            // Opt-out report scope — 'all' is only offered to (and honored for) SuperAdmins.
            optOutScope: 'self',
            isSuperAdmin: false,
        }
    },
    computed: {
        downloadTooltip() {
            const communityName = this.selectedCommunityName;
            return communityName 
                ? `Download report for ${communityName}` 
                : 'Download report';
        },
        selectedCommunityName() {
            if (this.selectedCommunity === 'all') return '';
            const community = this.communities.find(c => c._id === this.selectedCommunity);
            return community ? community.name : '';
        }
    },
    notifications: {
        showDownloadingMsg: {
            type: VueNotifications.types.success,
            message: "Export request sent.  Report will be downloaded in a moment."
        },
        showWarnMsg: {
            type: VueNotifications.types.warn,
            message: "No data found for export file"
        }
    },
    async created() {
        this.token = localStorage.getItem('token');
        try {
            const authUser = JSON.parse(localStorage.getItem('authUser') || 'null');
            this.isSuperAdmin = !!(authUser && authUser.role === 'SuperAdmin');
        } catch (e) {
            this.isSuperAdmin = false;
        }

        // Pre-select community from localStorage
        const communityIdParam = this.$route.params.community_id
            || this.$route.query.community_id
            || localStorage.getItem('user_modal_option');
        if (communityIdParam && communityIdParam !== 'all') {
            this.selectedCommunity = communityIdParam;
        }
        
        await this.loadData();
        await this.getCommunities();
        
        // Listen for community changes from the header
        this.$root.$on('community-selected', this.onHeaderCommunityChanged);
    },
    beforeDestroy() {
        this.$root.$off('community-selected', this.onHeaderCommunityChanged);
    },
    methods: {
        dateFilter(res, type) {
            let index = this.reports.findIndex(r => r.type == type);
            // Send the chosen calendar days as bare YYYY-MM-DD; the API (dateRange.helper.js)
            // snaps them to whole days, so the CSV holds only rows inside the selected range.
            // "All" (no preset) sends no bounds at all.
            const isAll = !res.selectedValue;
            const isCustom = res.selectedValue === 'custom';
            this.reports[index].startDate = isAll ? null : toDayParam(isCustom ? pickedDayToUtc(res.startDate) : res.startDate);
            this.reports[index].endDate = isAll ? null : toDayParam(isCustom ? pickedDayToUtc(res.endDate) : res.endDate);
		},
        loadData: async function() {
            await this.axios.get(`${process.env.VUE_APP_API_BASE_URL}/dashboard/all-reports/`,
            {headers: {'x-access-token': this.token}}).then(async response => {
                if (response.data) {
                   this.reports = response.data;
                }
            }).catch(err => {
                if (err.response.status  === 401) {
                    localStorage.removeItem('authUser');
                    this.$router.push('/login');
                }
            });
            this.$helpers.setTabindexOnTableCells();
        },
        
        // Load communities list (same pattern as responses.vue)
        async getCommunities() {
            try {
                const { data } = await this.axios.get(
                    `${process.env.VUE_APP_API_BASE_URL}/dashboard/sidebar-community`,
                    { headers: { 'x-access-token': this.token } }
                );
                this.communities = data;
                
                const communityIdParam = this.$route.params.community_id
                    || this.$route.query.community_id
                    || localStorage.getItem('user_modal_option');
                
                if (communityIdParam && communityIdParam !== 'all') {
                    const selectedCommunity = data.find(c => c._id === communityIdParam);
                    if (selectedCommunity) {
                        this.selectedCommunity = selectedCommunity._id;
                    }
                } else {
                    // Auto-select if only one community
                    if (this.communities.length === 1) {
                        this.selectedCommunity = this.communities[0]._id;
                    }
                }
                
                this.loadCirclesFromCommunity();
            } catch (err) {
                if (err.response && err.response.status === 401) {
                    localStorage.removeItem('authUser');
                    this.$router.push('/login');
                }
            }
        },
        
        // Load circles from the selected community's additionalParams
        loadCirclesFromCommunity() {
            this.selectedCircle = 'all';
            if (this.selectedCommunity === 'all' || !this.selectedCommunity) {
                this.circles = [];
                return;
            }
            const community = this.communities.find(c => c._id === this.selectedCommunity);
            if (community && community.additionalParams && community.additionalParams.length) {
                this.circles = community.additionalParams
                    .filter(circle => circle && circle.name)
                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            } else {
                this.circles = [];
            }
        },
        
        // Handle community dropdown change on this page
        onCommunityFilterChange() {
            this.loadCirclesFromCommunity();
        },
        
        // Handle community change from the header "Change Community" button
        onHeaderCommunityChanged(communityId) {
            if (communityId && communityId !== 'all') {
                this.selectedCommunity = communityId;
            } else {
                this.selectedCommunity = 'all';
            }
            this.loadCirclesFromCommunity();
        },
        
        exportData: async function(type) {
            let report = this.reports.find(r => r.type == type);

            // Empty bounds = all-time on the API side.
            const startDate = report.startDate ? encodeURIComponent(report.startDate) : '';
            const endDate = report.endDate ? encodeURIComponent(report.endDate) : '';

            const url = this.reportTypeUrl(type, startDate, endDate);

            const response = await this.fetchExportData(url);

            if(response && response.data) {
                await this.downloadCsv(type, response);
            }
        },
        
        reportTypeUrl: function(type, startDate, endDate) {
            const circleQuery = this.selectedCircle && this.selectedCircle !== 'all' ? `&circle=${this.selectedCircle}` : '';
            const communityQuery = this.selectedCommunity && this.selectedCommunity !== 'all' ? `&community=${this.selectedCommunity}` : '';
            
            let url = "";
            switch (type) {
                case reportType.Interaction:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/interaction-report?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
                case reportType.OptOut:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/member-opt-out-report?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}${this.isSuperAdmin && this.optOutScope === 'all' ? '&optOutScope=all' : ''}`
                    break;
                case reportType.OverDueRequests:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/requestOverDue?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
                case reportType.InvalidEmails:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/invalidEmail?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
                case reportType.NoFollowUp:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/noFollowUp/?export=csv&startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
                case reportType.DonorEngagement:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/donorEngagement?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
                case reportType.ProfileUpdates:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/members/memberProfileChanges?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
                case reportType.Referrals:
                    url = `${process.env.VUE_APP_API_BASE_URL}/dashboard/referrals/report?startDate=${startDate}&endDate=${endDate}${communityQuery}${circleQuery}`
                    break;
            }
            return url;
        },
        fetchExportData: async function(url) {
            try {
                this.showDownloadingMsg();
                this.announcement("Export request sent.  Report will be downloaded in a moment.");
                const authHeader =  {headers: {'x-access-token': this.token}};
                const response = await this.axios.get(url, authHeader)
                return response;
            } catch (error) {
                if (error.response.status  === 401) {
                    localStorage.removeItem('authUser');
                    this.$router.push('/login');
                }
            }
        },
        downloadCsv: async function(type, response) {
            switch (type) {
                case reportType.Interaction:
                    if (response.data && response.data.length) {
                        await exportCSV(response.data.slice(), 'interaction', ['responses'], [ 'date', 'response_date']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.OptOut:
                    if (response.data && response.data.length) {
                        await exportCSV(response.data.slice(), 'member-opt-out', [], ['date']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.OverDueRequests:
                    if (response.data && response.data.asks && response.data.asks.length) {
                        await exportCSV(response.data.asks.slice(), 'overdue-requests', ['title','requestId','memberId', 'commentCount'], ['date']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.InvalidEmails:
                    if (response.data && response.data.length) {
                        response.data.sort(function (a, b) {
                            return b.date.toLowerCase().localeCompare(a.date.toLowerCase());
                        });
                        await exportCSV(response.data.slice(), 'invalid-emails', [], ['date']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.NoFollowUp:
                    if (response.data && response.data.give && response.data.give.length) {
                        await exportCSV(response.data.give.slice(), 'no-follow-up', [], ['requestReceived', 'responseDate', 'responseFeedbackDate']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.DonorEngagement:
                    if (response.data && response.data.length) {
                        let donorData = this.$lodash.orderBy(response.data, ['sent'], ['desc']);
                        await exportCSV(donorData.slice(), 'donor-engagement', ['advisorId', 'communityId'], ['sent']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.ProfileUpdates:
                    if (response.data && response.data.profileChanges && response.data.profileChanges.length) {
                        await exportCSV(response.data.profileChanges.slice(), 'profileChanges', [], ['createdAt']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;
                case reportType.Referrals:
                    if (response.data && response.data.length) {
                        await exportCSV(response.data.slice(), 'referrals', [], ['date']);
                    } else {
                        this.showWarnMsg();
                    }
                    break;

            }
        },
        announcement(message){
            const liveRegion = document.createElement('div');
            liveRegion.setAttribute('aria-live', 'polite');
            liveRegion.setAttribute('role', 'alert');
            liveRegion.innerText = message;
            document.body.appendChild(liveRegion);
            setTimeout(() => {
                liveRegion.remove();
            },1000);
        },
		handleSearch(searchText) {
			// Use the search text from the child component to filter your table
			this.$refs.reportsTable.setFilter(searchText);
		},
		handleLimitChange(newLimit) {
			// Use the new limit value from the child to update your table
			this.$refs.reportsTable.setLimit(newLimit);
		}
    }
}
</script>
